import * as _ from 'lodash-es';
import { HttpClient } from '@repo/core/api';
import { Practice, PracticeViewModel, StoredFolder } from '../models';
import { ResourceConfig } from '@repo/core/resources';

const http = new HttpClient({ baseURL: ResourceConfig.clientPortalServerBase });

export const usePracticeService = () => {
  const current = async (): Promise<Practice> => {
    try {
      const response = await http.get(`practices/current?$expand=*`);
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const logo = async (): Promise<string | undefined> => {
    try {
      const response = await http.get(`practices/logo`);
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const folders = async (): Promise<StoredFolder[]> => {
    try {
      const response = await http.get(`practices/folders`);
      return setFolderData(response.data);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const deleteFolder = async (folderPath: string) => {
    try {
      folderPath = encodeURIComponent(folderPath);
      const response = await http.delete(`practices/deleteFolder?path=${folderPath}`);
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const renameFolder = async (folder: StoredFolder, newName: string) => {
    try {
      const response = await http.post(
        `practices/renameFolder?newName=${encodeURIComponent(_.toString(newName))}`,
        folder
      );
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const createFolder = async (path: string, updateEntities: boolean) => {
    try {
      const response = await http.post(
        `practices/createFolder?path=${encodeURIComponent(path)}&updateEntities=${updateEntities}`
      );
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const setFolderData = (folders: StoredFolder[]): StoredFolder[] => {
    folders = _.map(folders, (x) => {
      x.data = { name: x.name };
      return x;
    });
    _.forEach(folders, (folder) => {
      folder.children = setFolderData(folder.children);
    });
    return folders;
  };

  const post = async (data: Practice[], options?: any) => {
    if (!options) options = {};
    options.autoSave = options.autoSave != null ? options.autoSave : true;

    try {
      const response = await http.post(`practices`, data, { headers: options });
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const uploadLogo = async (logo: File) => {
    try {
      const formData = new FormData();
      formData.append('logo', logo);

      return await http.post(`practices/uploadLogo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const removeLogo = async () => {
    try {
      const response = await http.delete(`practices/removeLogo`);
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const updateAuditorFolder = async (folderName: string) => {
    try {
      const response = await http.post(`practices/updateAuditorFolder?folderName=${encodeURIComponent(folderName)}`);
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const updateClientUploadDefaultFolder = async (path: string) => {
    try {
      const response = await http.post(`practices/updateClientUploadDefaultFolder?path=${encodeURIComponent(path)}`);
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const detail = async (practiceId: string): Promise<PracticeViewModel> => {
    try {
      const response = await http.get(`practices/detail/${practiceId}`);
      return response.data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  return {
    current,
    logo,
    post,
    uploadLogo,
    removeLogo,
    folders,
    renameFolder,
    deleteFolder,
    createFolder,
    updateAuditorFolder,
    updateClientUploadDefaultFolder,
    detail
  };
};
